import { UploadOutlined } from '@ant-design/icons';
import { Button, message, Upload } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ClaimFormState, excludeRemovedDocuments } from '../../pages/newClaim/schema';
import { PreClaimFormState } from '../../pages/preClaim/schema';
import { MessageType } from '../../models/MessageType';
import { useEffect, useState } from 'react';
import { RcFile, UploadFile } from 'antd/es/upload';
import styled from 'styled-components';

export const A_MB = 1024 * 1024;
export const TEN_MB = 10 * A_MB;
export const DEFAULT_MAX_FILE_SIZE = TEN_MB;
export const DEFAULT_MAXIMUM_FILES = 12;

interface UploadInputProps {
  name: keyof ClaimFormState | keyof PreClaimFormState;
  maximumFileSize?: number;
  maximumFiles?: number;
  disabled?: boolean;
  allowRemoveDocuments?: boolean;
  accept?: string;
  notShowUplist?: boolean;
  showButton?: boolean;
  onPreview?: (file: UploadFile) => void;
}

export interface FileUploaded extends RcFile {
  status?: string;
}

export const hasUploadedFile = (existingFiles: RcFile[], file: RcFile) => {
  return excludeRemovedDocuments(existingFiles).find((f: RcFile) => f.size === file.size && f.type === file.type);
};

const StyledUpload = styled(Upload)`
  .ant-upload-list-item-name {
    cursor: pointer;
  }

  .ant-upload-list-item-card-actions-btn {
    cursor: pointer;
  }
`;

export const UploadInput = ({
  name,
  maximumFiles = DEFAULT_MAXIMUM_FILES,
  maximumFileSize = DEFAULT_MAX_FILE_SIZE,
  allowRemoveDocuments = true,
  disabled = false,
  accept,
  notShowUplist,
  showButton = true,
  onPreview,
}: UploadInputProps) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<ClaimFormState | PreClaimFormState>();
  const [internalDisabled, setInternalDisabled] = useState(false);
  const values = watch(name);

  useEffect(() => {
    if (excludeRemovedDocuments(values)?.length >= maximumFiles && maximumFiles != 1) {
      message[MessageType.WARNING](t('common.error.noOfFilesExceedZdy', { amount: maximumFiles }));
      setInternalDisabled(true);
    } else {
      setInternalDisabled(false);
    }
  }, [t, maximumFiles, values]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <StyledUpload
          accept={accept}
          fileList={excludeRemovedDocuments(value as FileUploaded[]) as RcFile[]}
          beforeUpload={(file) => {
            // 检查文件类型和扩展名
            // const fileName = file.name.toLowerCase();
            const isImage = file.type.indexOf('image/') === 0;
            const isPdf = file.type === 'application/pdf';
            // const isHeic = fileName.endsWith('.heic') || fileName.endsWith('.heif');
            const isValidType = isImage || isPdf;
            // || isHeic;

            if (!isValidType) {
              message[MessageType.ERROR](t('common.error.notPdfOrImages'));
              return Upload.LIST_IGNORE;
            }
            if (file.size > maximumFileSize) {
              message[MessageType.ERROR](t('common.error.sizeExceeds', { amount: TEN_MB / A_MB }));
              return Upload.LIST_IGNORE;
            }

            const currentFiles = excludeRemovedDocuments(value as FileUploaded[]);

            // 检查文件数量限制
            if (currentFiles.length >= maximumFiles) {
              message[MessageType.ERROR](t('common.error.noOfFilesExceedZdy', { amount: maximumFiles }));
              return Upload.LIST_IGNORE;
            }

            if (hasUploadedFile(currentFiles, file)) {
              message[MessageType.ERROR](t('common.error.notAllowUploadSameDocument'));
              return Upload.LIST_IGNORE;
            }

            if (!value) {
              onChange([file]);
            } else {
              // if (hasUploadedFile(value, file)) {
              //   message[MessageType.ERROR](t('common.error.notAllowUploadSameDocument'));
              //   return Upload.LIST_IGNORE;
              // }
              Array.isArray(value) && onChange([...value, file]);
            }
            return false;
          }}
          onRemove={(file) => {
            if (Array.isArray(value)) {
              const foundIndex = value.findIndex((o) => o.uid === file.uid);
              value[foundIndex].status = 'removed';
              onChange([...value]);
            }
          }}
          onPreview={onPreview}
          listType="picture"
          showUploadList={notShowUplist ? false : { showRemoveIcon: allowRemoveDocuments }}
        >
          {showButton && (
            <Button disabled={internalDisabled || disabled} icon={<UploadOutlined />}>
              {t('newClaim.supplementaryDocuments.upload')}
            </Button>
          )}
        </StyledUpload>
      )}
    />
  );
};
